import { useState } from 'react'

import { isDemoMode, type PMComponentRef } from '@axilon-apps/domains-ontology-ui-state'

import { ComponentChip } from '../primitives/ComponentChip'
import { TIER_PILL_CLASSES, severityTier } from '../primitives/severityTier'
import { cn } from '../../lib/utils'
import type { TriageState } from './types'
import { ReviewStateButton } from './ReviewStateButton'

export interface RootCauseCardProps {
  title: string
  reasoning: string
  /** [0, 1] — RCA root_cause confidence. */
  confidence: number
  components?: PMComponentRef[]
  /** Additional components emitted as `downstream` of this root cause. */
  downstream?: PMComponentRef[]
  state?: TriageState
  /** Operator-supplied correction text persisted alongside `state === 'denied'`. */
  denialNotes?: string
  /**
   * For 'denied' transitions, `denialNotes` is the operator's correction —
   * required by the API and prompted for via an inline composer.
   */
  onSetState?: (next: TriageState, denialNotes?: string) => void
  /** Revision / pending-reason badge under the title (follow-up chrome). */
  flag?: string | null
  /**
   * Deny marks the card as needing a reason on the Agent tab instead of
   * opening the inline composer. Nothing is persisted until that send.
   */
  reasonOnAgentTab?: boolean
  /** Lights the Deny control while a reason is still pending. */
  pendingDeny?: boolean
  /** Latest observer check verdict for this candidate. */
  sightingOutcome?: 'support' | 'refute' | 'unknown'
}

/**
 * Card for one RCA root cause. Title + Accept/Deny + Origin + Impacts
 * are always visible — the operator reads the structured surface at a
 * glance. The longform `reasoning` collapses behind the title via a
 * smooth grid-rows transition so dense incidents stay scannable.
 */
export function RootCauseCard({
  title,
  reasoning,
  confidence,
  components,
  downstream,
  state = 'pending',
  denialNotes,
  onSetState,
  flag,
  reasonOnAgentTab = false,
  pendingDeny = false,
  sightingOutcome
}: RootCauseCardProps) {
  const tier = severityTier(confidence)
  const pct = Math.round(Math.max(0, Math.min(1, confidence)) * 100)
  const hasReasoning = reasoning.trim().length > 0
  // Expandable when there's anything behind the chevron — reasoning OR the
  // component chips (Origin / Impacts), which are now collapsed by default.
  const hasDetail = hasReasoning || (components?.length ?? 0) > 0 || (downstream?.length ?? 0) > 0
  const [expanded, setExpanded] = useState(false)
  // Inline deny composer — only renders while the operator is drafting a
  // reason for a not-yet-denied row.
  const [denying, setDenying] = useState(false)
  const [denialDraft, setDenialDraft] = useState('')
  const demoDenialText =
    'That valve was in manual for scheduled CIP; the manifold was isolated, so sanitizer could not bleed into production.'
  return (
    <div
      className={cn(
        'rounded-md border bg-bg-elev p-4 transition-colors duration-200 ease-out',
        state === 'accepted' && 'border-st-run bg-st-run-bg/40 ring-1 ring-st-run/30',
        state === 'denied' && 'border-line opacity-60',
        state !== 'accepted' && state !== 'denied' && (flag ? 'border-brand' : 'border-line')
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-tour-id="root-cause-expand"
          onClick={() => hasDetail && setExpanded((v) => !v)}
          aria-expanded={expanded}
          disabled={!hasDetail}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 text-left',
            hasDetail && 'cursor-pointer'
          )}
        >
          <h4
            className={cn(
              'm-0 min-w-0 truncate text-base font-semibold text-ink',
              state === 'denied' && 'line-through text-ink-3'
            )}
          >
            {title}
          </h4>
          <span
            className={cn(
              'shrink-0 rounded-full px-2 py-px text-10 font-medium uppercase tracking-wide',
              TIER_PILL_CLASSES[tier]
            )}
          >
            {pct}%
          </span>
          {sightingOutcome && sightingOutcome !== 'unknown' ? (
            <span
              className={cn(
                'shrink-0 rounded-full border px-2 py-px text-10 font-medium',
                sightingOutcome === 'support'
                  ? 'border-st-run/40 bg-st-run-bg text-st-run'
                  : 'border-st-down/40 bg-st-down-bg text-st-down'
              )}
            >
              check {sightingOutcome === 'support' ? 'supports' : 'refutes'}
            </span>
          ) : null}
          {hasDetail && (
            <span
              aria-hidden
              className={cn(
                'shrink-0 text-ink-3 transition-transform duration-300 ease-out',
                expanded && 'rotate-180'
              )}
            >
              ▾
            </span>
          )}
        </button>
        {onSetState && (
          <div
            className="ml-auto flex shrink-0 items-center gap-1.5 text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <ReviewStateButton
              isActive={state === 'accepted'}
              isMuted={state === 'denied' || pendingDeny}
              tone="accept"
              onClick={() => onSetState(state === 'accepted' ? 'pending' : 'accepted')}
            >
              Accept
            </ReviewStateButton>
            <ReviewStateButton
              dataTourId="root-cause-deny"
              isActive={state === 'denied' || pendingDeny}
              isMuted={state === 'accepted'}
              tone="deny"
              onClick={() => {
                if (reasonOnAgentTab) {
                  onSetState(pendingDeny || state === 'denied' ? 'pending' : 'denied')
                  return
                }
                if (state === 'denied') {
                  onSetState('pending')
                  setDenying(false)
                  setDenialDraft('')
                  return
                }
                setDenying((v) => {
                  const next = !v
                  if (next && isDemoMode() && denialDraft.trim().length === 0) {
                    setDenialDraft(demoDenialText)
                  }
                  return next
                })
              }}
            >
              Deny
            </ReviewStateButton>
          </div>
        )}
      </div>

      {flag ? (
        <div className="mt-2 inline-flex items-center rounded bg-brand-soft px-2 py-0.5 font-mono text-11 font-medium leading-relaxed text-brand">
          {flag}
        </div>
      ) : null}

      {hasDetail && (
        <div
          data-tour-id="root-cause-detail"
          className="grid transition-[grid-template-rows] duration-300 ease-out"
          style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            {hasReasoning && (
              <p className="m-0 pt-3 text-sm leading-relaxed text-ink-2 whitespace-pre-wrap">
                {reasoning}
              </p>
            )}
            {components && components.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="text-11 uppercase tracking-wide text-ink-3">Origin</span>
                {components.map((c) => (
                  <ComponentChip key={`rc:${c.scope}:${c.id}`} component={c} />
                ))}
              </div>
            )}
            {downstream && downstream.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-11 uppercase tracking-wide text-ink-3">Impacts</span>
                {downstream.map((c) => (
                  <ComponentChip key={`ds:${c.scope}:${c.id}`} component={c} hideScope />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {onSetState && denying && state !== 'denied' && (
        <div
          className="mt-3 flex flex-col gap-2 rounded-md border border-st-down/40 bg-st-down-bg/20 p-3"
          onClick={(e) => e.stopPropagation()}
        >
          <label className="text-11 font-medium uppercase tracking-wide text-st-down">
            Why is this wrong? Your correction trains the agent.
          </label>
          <textarea
            autoFocus
            rows={3}
            value={denialDraft}
            onChange={(e) => setDenialDraft(e.target.value)}
            placeholder="e.g. the real cause was upstream throttling, not this service…"
            className="w-full resize-y rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-st-down focus:outline-none focus:ring-2 focus:ring-st-down/30"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const trimmed = denialDraft.trim()
                if (!trimmed) return
                onSetState('denied', trimmed)
                setDenying(false)
                setDenialDraft('')
              }}
              disabled={denialDraft.trim().length === 0}
              className={cn(
                'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold',
                'transition-[background-color,border-color,opacity] duration-150 ease-out',
                denialDraft.trim().length > 0
                  ? 'cursor-pointer border-st-down bg-st-down text-bg hover:opacity-90'
                  : 'cursor-not-allowed border-line bg-bg-sunk text-ink-3'
              )}
            >
              Confirm deny
            </button>
            <button
              type="button"
              onClick={() => {
                setDenying(false)
                setDenialDraft('')
              }}
              className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-line bg-bg px-2.5 py-1 text-xs text-ink-2 hover:bg-bg-sunk"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {state === 'denied' && denialNotes && (
        <div className="mt-3 rounded-md border border-st-down/40 bg-st-down-bg/30 p-3">
          <div className="mb-1 text-10 font-medium uppercase tracking-wide text-st-down">
            Denial reason
          </div>
          <p className="m-0 whitespace-pre-wrap text-sm text-ink-2">{denialNotes}</p>
        </div>
      )}
    </div>
  )
}
